import { fetchArrayOfObjects } from '@api/api_calls'
import { ANIMATED_BACKGROUND_ENABLED } from '@classes/animation_settings'
import Pixel from '@classes/Pixel'
import type { SatelliteAntenna } from '@constants/types'
import type { ChannelMode } from '@containers/admin/InfoChannel/WidgetLayout'
import type { Widget } from '@containers/admin/Widgets/CustomWidget'
import { TYPE } from '@containers/DemoPage/DemoPage'
import { getServerUrl } from '@hooks/useApi'
import Logger from '@logger'
import type { AntennasConfig, ConfigData, PageSetting, PDFFile, SafetyPlan } from '@shared_types/config_types'
import { type ShipOwnerVideo } from '@shared_types/data_files/index_json_types'
import type { WidgetConfigData, WidgetStub } from '@shared_types/guest_info_types'
import { type ModbusAddressConfigured } from '@shared_types/modbus_types'
import { asArray, isNonEmptyArray } from '@shared_utils/array_utils'
import { callApi } from '@utils/call_api'
import { type AxiosResponse } from 'axios'
import { get, isBoolean, isNumber, isObject, isString, map, set } from 'lodash'

const logger = Logger.create('config')

let configData: ConfigData | null = null
let widgetData: WidgetConfigData | null = null
let defaultPages: string[] = []
let pages: PageSetting[] = []
let configuredModbusAddresses: ModbusAddressConfigured[] = []

interface ICachedResponse {
  timestamp: number
  data: AxiosResponse
}

const fetchCache = new Map<string, ICachedResponse>()

const fetchCachedFor500ms = async (path: string) => {
  const lastResponse = fetchCache.get(path)

  if (lastResponse && Date.now() - lastResponse.timestamp < 500) {
    return lastResponse.data
  }

  const url = getServerUrl(path, { ts: Date.now() })
  const data = await callApi(url)
  fetchCache.set(path, { timestamp: Date.now(), data })
  return data
}

const getConfig = () => callApi('/storage/index.json')

const getPagesConfig = async () => {
  const pagesConf = await fetchCachedFor500ms('/storage/pages.json')
  return pagesConf as PageSetting[]
}

const getWidgetConfig = () => fetchCachedFor500ms('/storage/widgets.json')

export const setupWidget = async () => {
  widgetData = await getWidgetConfig()
  return widgetData
}

// this would be named better if the context was ever clear
const getListOfWidgets = (stubs: WidgetStub[], fulls: Widget[]): Widget[] => {
  return stubs.map(stub => {
    const fullWidget = fulls.find(widget => `${widget.id}` === `${stub.id}`)
    return { ...fullWidget, ...stub } as unknown as Widget
  })
}

export const getWidgetValue = (mode: 'tv' | 'desktop', id?: number): { widgets: Widget[]; screen?: number } => {
  if (!widgetData) {
    return { widgets: [] }
  }

  let widgetsIds = null

  if (id?.toString() && mode !== 'desktop') {
    if (mode in widgetData.infoChannel) {
      widgetsIds = widgetData.infoChannel[mode][id]
    } else {
      return { widgets: [] }
    }
  } else {
    widgetsIds = widgetData.infoChannel[mode]
  }

  const widgets = map(widgetData.widgets, (item, id) => {
    return { id, ...item } as unknown as Widget
  })

  if (widgetsIds && widgetsIds.content && mode !== 'desktop') {
    // no content?
    return {
      widgets: getListOfWidgets(widgetsIds.content, widgets),
      screen: widgetData.infoChannel[mode].length
    }
  } else if (widgetsIds && !widgetsIds.content) {
    return { widgets: getListOfWidgets(widgetsIds, widgets) }
  } else {
    return { widgets: [] }
  }
}

export const getPresentationWidgetValue = (presentationId?: string, presentationIndex?: number): { widgets: Widget[]; screen?: number } => {
  if (!widgetData) {
    return { widgets: [] }
  }

  let widgetsIds = null

  if (presentationIndex?.toString()) {
    if (presentationId && Array.isArray(widgetData.presentations[presentationId]?.views)) {
      widgetsIds = widgetData.presentations[presentationId].views[presentationIndex]
    } else {
      return { widgets: [] }
    }
  }

  const widgets = map(widgetData.widgets, (item, id) => {
    return { id, ...item } as unknown as Widget
  })

  if (presentationId && widgetsIds && widgetsIds.content) {
    return {
      widgets: getListOfWidgets(widgetsIds.content, widgets),
      screen: widgetData.presentations[presentationId].views.length
    }
  } else if (widgetsIds && !widgetsIds.content) {
    return { widgets: getListOfWidgets(widgetsIds, widgets) }
  } else {
    return { widgets: [] }
  }
}

export const getAllPDFs = () => get(configData, 'model.pdf', []) as PDFFile[]

export const getAllDesktops = (mode: ChannelMode) => {
  return widgetData ? widgetData.infoChannel[mode] : []
}

export const getAllPresentations = () => {
  return widgetData?.presentations ? Object.keys(widgetData.presentations).map(key => ({ ...widgetData?.presentations[key], id: key })) : []
}

export const getViewsByPresentationId = (presentationId: string) => {
  return widgetData ? widgetData.presentations[presentationId]?.views : []
}

export const getWidgetById = (widgetId: string) => {
  if (!widgetData?.widgets) {
    return null
  }
  const widgets = map(widgetData.widgets, (item, id) => ({ id, ...item }))
  return widgets.find(item => item.id === widgetId)
}

export const getAllWidgets = () => {
  if (!widgetData) {
    return []
  }
  return map(widgetData.widgets, (item, id) => {
    return { id, ...item } as unknown as Widget
  })
}

export const getInfoChannelWidgets = (mode: 'desktop' | 'tv') => {
  return get(widgetData, `infoChannel.${mode}`)
}

setupWidget()
  .then(() => logger.debug('did setup widget'))
  .catch(err => logger.error(`could not setup widget: ${err}`))

export const loadAndStoreConfig = async () => {
  configData = await getConfig()
  configuredModbusAddresses = await fetchArrayOfObjects<ModbusAddressConfigured>('/api/modbus/registers')
  return configData
}

export const loadAndStorePagesConfig = async () => {
  pages = await getPagesConfig()
  return pages as PageSetting[]
}

function getConfigValueByKeyPath (keyPath: string) {
  return get(configData as ConfigData, keyPath)
}

export function getConfigString (keyPath: string, fallback = '') {
  const value = getConfigValueByKeyPath(keyPath)
  return isString(value) ? value : fallback || ''
}

export function getThemeName (defaultName = 'default') {
  return getConfigString('theme.name') || defaultName
}

export function isUsingDarkTheme () {
  return getThemeName() === 'obsidian'
}

export function getConfigUrl (keyPath: string, showUrl = false): string {
  const value = getConfigString(keyPath)

  if (!value) {
    return ''
  } else if (value.startsWith('http') || showUrl) {
    return value
  } else {
    return getServerUrl(value)
  }
}

export function getConfigNumber (keyPath: string, fallback = 0) {
  const value = getConfigValueByKeyPath(keyPath)
  return isNumber(value) ? value : fallback || 0
}

export function getConfigObject<T> (keyPath: string, fallback?: T): T {
  const value = getConfigValueByKeyPath(keyPath)
  return (value && isObject(value) ? value : fallback) as T
}

export function getConfigArray<InType> (keyPath: string, fallback: InType[] = []): InType[] {
  const value = getConfigValueByKeyPath(keyPath)
  return asArray<InType>(value || fallback)
}

export function getConfigBoolean (keyPath: string, fallback?: boolean): boolean {
  const value = getConfigValueByKeyPath(keyPath)
  return isBoolean(value) ? value : fallback || false
}

export const getConfigValueMax = (configPath: string) => getConfigNumber(`vessel.${configPath}.max`)

interface TankConfig {
  max: number
  scale?: number
  cubicMeters?: boolean
}

export function getTankConfig (keyPath: string) {
  const tank = getConfigObject<TankConfig>(keyPath)
  const scale = tank.scale ?? 1000
  const unit = (tank.cubicMeters || scale === 1000) ? '\u33A5' : 'l'
  const maxValue = tank.max / scale
  return { tank, scale, unit, maxValue }
}

export function getSafetyPlans () {
  return getConfigArray<SafetyPlan>('model.safetyPlan')
}

export function getPDFs () {
  return getConfigArray<PDFFile>('model.pdf')
}

export function getShipImages () {
  return getConfigArray<string>('model.static.shipImage')
}

export function getShipVideos () {
  return getConfigArray<ShipOwnerVideo>('model.video.shipOwner')
}

export function getGauges () {
  return getConfigArray<{ name: string }>('gauge.layout')
}

export const AppConfig = {
  getAll: () => configData as ConfigData,
  '3d': {
    main: () => getConfigString('3d.model.name'),
    demo: () => getConfigString('3d.model.demo.name'),
    minZoomLevel: () => getConfigNumber('tvMode.minZoomLevel', -1),
    maxZoomLevel: () => getConfigNumber('tvMode.maxZoomLevel', -1)
  },
  tvMode: {
    get autoExplode () {
      return getConfigNumber('tvMode.threeD.autoExplode')
    },
    set autoExplode (seconds: number) {
      const intVal = Math.max(seconds, 0)
      if (configData) {
        set(configData, 'tvMode.threeD.autoExplode', intVal)
      }
    }
  },
  getGaugeGridLayout: () => getConfigString('gauge.mode.grid'),
  offlineMaps: {
    enabled: () => getConfigBoolean('model.offlineMap.isOffline')
  },
  weatherMaps: {
    enabled: () => getConfigBoolean('model.weatherMap.enabled')
  }
}

export const getSatelliteAntennas = (): Partial<SatelliteAntenna>[] => {
  const antennas = getConfigObject<AntennasConfig>('model.antennas')
  return map(antennas, (antenna, key) => {
    const { type } = antenna
    return {
      key,
      label: type,
      type,
      description: `${type} Description`,
      image: getConfigUrl(`model.antennas.${key}.file`) || ''
    }
  })
}

const isDemoVideoActive = (videos: ShipOwnerVideo[]) =>
  asArray(videos).some(video => video.isActive)

export const shouldShowDemoButton = (): boolean => {
  if (isPageDisabled('demoButton') || isPageDisabled('video')) {
    return false
  }

  const videos = getConfigArray<ShipOwnerVideo>(`model.video.${TYPE.VIDEO}`)
  const photos = getConfigArray<string>(`model.static.${TYPE.PHOTO}`)
  const demoModel = getConfigString('3d.model.demo.name')

  return isNonEmptyArray(photos) || isDemoVideoActive(videos) || demoModel.toLowerCase() !== 'none'
}

export const getModbusAddressByName = (name: string) => {
  return configuredModbusAddresses.find(a => a.name === name)
}

export const isModbusRegisterConfigured = (registerName: string) => {
  const address = getModbusAddressByName(registerName)?.address
  return isNumber(address) && address >= 0
}

export const setupDefaultPages = async (): Promise<string[]> => {
  const url = getServerUrl('/defaults/default_pages.json')
  defaultPages = await callApi(url)
  return defaultPages
}

export const setupPages = async (): Promise<PageSetting[]> => {
  const url = getServerUrl('/storage/pages.json')
  pages = await callApi(url)
  return pages as PageSetting[]
}

export const getAllPageNames = (): string[] => {
  return defaultPages ?? []
}

export function getPages (): PageSetting[] {
  const initialPages = getAllPageNames()

  if (!pages || !pages.length) {
    return []
  }

  pages.forEach((setting, index: number) => {
    if (
      setting.page
      && (isString(setting.page)
        ? !initialPages.includes(setting.page)
        : !initialPages.some((r: string) => setting.page.includes(r)))
    ) {
      pages[index].hidden = true

      pages
        .filter(p => !!p.parent?.startsWith(setting.key))
        .forEach(children => {
          if (pages.some(p => p.key === children.key)) {
            pages[index].hidden = true
          }
        })
    }
  })

  return pages
}

export function isPageDisabled (key: string) {
  if (key === 'MechanicalComponents') {
    key = 'mechanical-components'
  } else {
    key = key.toLowerCase()
  }
  if (!Array.isArray(pages)) {
    console.log('pages must be an array', pages)
  }
  return asArray(pages).some(page => page.key.toLowerCase() === key && page.disabled)
}

export const setBackground = (yisTheme: string, backgroundColor?: string) => {
  if (yisTheme !== 'default') {
    const canvas = document.getElementById('background')
    if (canvas) {
      canvas.style.background = backgroundColor ?? ''
      canvas.style.opacity = '0.0'
    }
    return null
  }

  const BACKGROUND_ANIMATION_FPS = 15

  logger.verbose(`Start animation with ${BACKGROUND_ANIMATION_FPS} fps`)

  const canvas = document.getElementById('background') as HTMLCanvasElement | null
  if (!canvas) {
    return null
  }
  canvas.style.opacity = '0.3'
  const ctx = canvas.getContext('2d')
  if (ctx) {
    const pixels = [new Pixel(0, 0), new Pixel(1, 0), new Pixel(0, 1), new Pixel(1, 1)]

    const animateBackground = () => {
      const is3dView = (window.location.pathname || '').includes('/3d_view')
      if (!is3dView) {
        pixels.forEach(function (pixel) {
          pixel.update()
          pixel.render(ctx)
        })
      }
    }

    if (ANIMATED_BACKGROUND_ENABLED) {
      return setInterval(animateBackground, 1000 / BACKGROUND_ANIMATION_FPS)
    }
  }

  return null
}

export const MAIN_ADMIN_ROUTE = '/admin'
